import { motion } from "framer-motion";
import { useMutation } from "@tanstack/react-query";
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { subscribeToNewsletter } from '@/services/newsletterService';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/context/LanguageContext';

const Newsletter: React.FC = () => {
  const [email, setEmail] = useState('');
  const { toast } = useToast();
  const { t } = useLanguage();

  const subscribeMutation = useMutation({
    mutationFn: (email: string) => subscribeToNewsletter({ email }),
    onSuccess: () => {
      setEmail('');
      toast({
        title: t('newsletter.success.title'),
        description: t('newsletter.success.description'),
      });
    },
    onError: (error) => {
      console.error('Error subscribing to newsletter:', error);
      toast({
        title: t('newsletter.error.title'),
        description: t('newsletter.error.description'),
        variant: "destructive",
      });
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    subscribeMutation.mutate(email);
  };

  return (
    <section className="section bg-black">
      <div className="container-custom max-w-4xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title text-white mb-6">
            {t('newsletter.title.start')}{' '}
            <span className="text-furniture-gold">{t('newsletter.title.highlight')}</span>
          </h2>
          <p className="section-subtitle text-white/80 mb-8">
            {t('newsletter.subtitle')}
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('newsletter.email.placeholder')}
              disabled={subscribeMutation.isPending}
              className="flex-1 px-6 py-4 bg-white/10 border border-white/20 text-white placeholder-white/60 font-sans rounded-md focus:outline-none focus:border-furniture-gold focus-visible:ring-2 focus-visible:ring-furniture-gold transition-colors duration-300 disabled:opacity-50"
            />
            <Button
              type="submit"
              variant="luxury"
              size="lg"
              disabled={subscribeMutation.isPending}
              className="px-8 py-4 h-auto shadow-luxury"
            >
              {subscribeMutation.isPending ? t('newsletter.button.loading') : t('newsletter.button.subscribe')}
            </Button>
          </form>

          <p className="font-sans text-sm text-white/60 mt-4">
            {t('newsletter.privacy')}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Newsletter;
